import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { searchSkillListingQueryInit, addBatchMatches } from "../../redux/actions";
import "./QueryPage.css";
import QueryPageHeader from "./components/organisms/QueryPageHeader";
import FilterDrawer from "./components/organisms/FilterDrawer";
import TalentSection from "./components/organisms/TalentSection";
import Pagination from "./components/organisms/Pagination";
import PlaceBidModal from "./components/organisms/PlaceBidModal";
import SelectJobModal from "./components/organisms/SelectJobModal";
import MarketplaceTalentCard from "./components/organisms/MarketplaceTalentCard";
import QueryEmptyState from "./components/organisms/QueryEmptyState";
import Loader from "../../components/Loading/Loader";
import { MarketplaceTalentIcon } from "./QueryPageIcons";
import {
  INITIAL,
  getActiveFilterCount,
} from "./components/organisms/FilterDrawerPanels/filterOptions";
import { buildMarketplaceFiltersPayload } from "./filterPayloadMappers";
import {
  buildQueryResultSearch,
  parseQueryResultState,
} from "./queryStateUrl";
import { canUseSubscriptionFeatures } from "../../shared/billing";

const PAGE_SIZE = 20;

const MarketplaceTalentResultsPage = () => {
  const { searchValue } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures();
  const [selectedTalentIds, setSelectedTalentIds] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(INITIAL);
  const [matchesOpen, setMatchesOpen] = useState(false);
  const [bidModal, setBidModal] = useState({
    open: false,
    talent: null,
    talentType: null,
    skillListingId: null,
    skillListingIds: null,
  });

  const searchTerm = searchValue ? decodeURIComponent(searchValue) : "RN Med Surg";
  const [queryState, setQueryState] = useState(() =>
    parseQueryResultState(location.search)
  );
  const { filters, sortOrder, currentPage } = queryState;
  const isAiMode = useSelector((state) => state.nlSearchExternalReducer.isActive);
  const {
    data: talents,
    loading,
    totalCount: rawTotalCount,
  } = useSelector((state) => state.queryPageSkillListingReducer);
  const safeTalents = Array.isArray(talents) ? talents : [];

  const displayedTalents = safeTalents;
  const displayedLoading = loading;
  const displayedTotalCount = rawTotalCount || safeTalents.length;
  console.info(displayedTotalCount,'displayedTotalCount')

  const updateQueryState = (nextState = {}) => {
    const nextSearch = buildQueryResultSearch({
      filters: nextState.filters ?? filters,
      sortOrder: nextState.sortOrder ?? sortOrder,
      currentPage: nextState.currentPage ?? currentPage,
    });
    const nextQueryState = parseQueryResultState(nextSearch);

    if (nextSearch !== location.search) {
      history.push({
        pathname: location.pathname,
        search: nextSearch,
      });
    }

    setQueryState(nextQueryState);
    return nextQueryState;
  };

  const fetchMarketplaceTalents = (page, nextFilters, nextSortOrder) => {
    dispatch(
      searchSkillListingQueryInit({
        search: searchTerm,
        perPage: PAGE_SIZE,
        usePmtTalentsSearch: true,
        skillName: searchTerm,
        ...(isAiMode && { aiSearch: true }),
        ...buildMarketplaceFiltersPayload(nextFilters, page, nextSortOrder),
      })
    );
  };

  const handleResetFilters = () => {
    try {
      setDraftFilters(INITIAL);
      setSelectedTalentIds([]);
      setFilterOpen(false);
      const nextState = updateQueryState({ filters: INITIAL, currentPage: 1 });
      fetchMarketplaceTalents(
        nextState.currentPage,
        nextState.filters,
        nextState.sortOrder
      );
    } catch (error) {
      console.error("Failed to reset marketplace talent filters:", error);
    }
  };

  useEffect(() => {
    fetchMarketplaceTalents(currentPage, filters, sortOrder);
  }, []);

  const openFilterDrawer = () => {
    setDraftFilters(filters);
    setFilterOpen(true);
  };

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }

    history.push(`/query/${encodeURIComponent(searchTerm)}`);
  };

  const totalPages = Math.max(1, Math.ceil(displayedTotalCount / PAGE_SIZE));
  const visibleTalentIds =
    displayedTalents.length > 0 ? displayedTalents.map((talent) => talent.id || talent._id) : [];
  const allSelected =
    visibleTalentIds.length > 0 &&
    visibleTalentIds.every((id) => selectedTalentIds.includes(id));

  if (displayedLoading) return <Loader />;

  return (
    <div className="qp-container">
      <QueryPageHeader
        totalMatches={displayedTotalCount}
        searchTerm={searchTerm}
        sortOrder={sortOrder}
        onChangeSortOrder={(nextSortOrder) => {
          setSelectedTalentIds([]);
          const nextState = updateQueryState({
            sortOrder: nextSortOrder,
            currentPage: 1,
          });
          fetchMarketplaceTalents(
            nextState.currentPage,
            nextState.filters,
            nextState.sortOrder
          );
        }}
        filterCount={getActiveFilterCount(filters)}
        onOpenFilter={openFilterDrawer}
        showSelectAll={!subscriptionFeaturesEnabled && displayedTalents.length > 0}
        allSelected={allSelected}
        onToggleSelectAll={() => {
          setSelectedTalentIds((prev) => {
            if (allSelected) {
              return prev.filter((id) => !visibleTalentIds.includes(id));
            }

            return [...new Set([...prev, ...visibleTalentIds])];
          });
        }}
        iconEmoji={<MarketplaceTalentIcon />}
        iconClass="qp-icon-marketplace"
        titleText="Skillgigs Marketplace Talent"
        onBack={handleBack}
      />

      {selectedTalentIds.length > 0 && (
        <div className="qp-bulk-bar">
          <button className="qp-bulk-btn qp-bulk-btn-cancel" type="button"
            onClick={() => setSelectedTalentIds([])}>
            Cancel
          </button>
          <button className="qp-bulk-btn" type="button" onClick={() => setMatchesOpen(true)}>
            Add to matches ( {selectedTalentIds.length} Skill listings )
          </button>
          <button className="qp-bulk-btn qp-bulk-btn-primary" type="button"
            onClick={() => setBidModal({ open: true, talent: { name: '', title: '', avatar: null, initials: '' }, talentType: 'marketplace', skillListingId: null, skillListingIds: selectedTalentIds })}>
            Place a bid ( {selectedTalentIds.length} Skill listings )
          </button>
        </div>
      )}

      <FilterDrawer
        open={filterOpen}
        values={draftFilters}
        onChange={setDraftFilters}
        onClose={() => setFilterOpen(false)}
        onReset={handleResetFilters}
        hideCompanyPanel
        hideJobTitleAdvancedFields
        onApply={(nextFilters) => {
          setSelectedTalentIds([]);
          setFilterOpen(false);
          const nextState = updateQueryState({
            filters: nextFilters || INITIAL,
            currentPage: 1,
          });
          fetchMarketplaceTalents(
            nextState.currentPage,
            nextState.filters,
            nextState.sortOrder
          );
        }}
      />

      <TalentSection
        iconEmoji={<MarketplaceTalentIcon />}
        iconClass="qp-icon-marketplace"
        titleText="Skillgigs Marketplace Talent"
        count={displayedTotalCount}
        showHeader={false}
      >
        {displayedTalents.length === 0 ? (
          <QueryEmptyState onAction={handleResetFilters} searchQuery={searchTerm} talentType="market place" />
        ) : (
          displayedTalents.map((talent) => {
            const talentId = talent.id || talent._id;

            return (
              <MarketplaceTalentCard
                key={talentId}
                talent={talent}
                profileUrl={`/internal-detail/${talentId}`}
                onViewProfile={(id) => history.push(`/profiledetail/${id}`)}
                onPlaceBid={(talentItem, talentType) =>
                  setBidModal({
                    open: true,
                    talent: talentItem,
                    talentType,
                    skillListingId:
                      talentItem?.skillListingId ??
                      talentItem?.SkillListingId ??
                      talentItem?.id ??
                      talentItem?._id ??
                      null,
                  })
                }
                selectable={!subscriptionFeaturesEnabled}
                selected={selectedTalentIds.includes(talentId)}
                onToggleSelect={(id) =>
                  setSelectedTalentIds((prev) =>
                    prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
                  )
                }
              />
            );
          })
        )}
      </TalentSection>

      <SelectJobModal
        open={matchesOpen}
        onClose={() => setMatchesOpen(false)}
        onSelect={(job) => {
          dispatch(addBatchMatches({ GigId: job.JobOrderId, JobTitle: job.title, SkillListingIds: selectedTalentIds }))
          setMatchesOpen(false)
          setSelectedTalentIds([])
        }}
        showVmsFacilityFilter={false}
      />

      <PlaceBidModal
        open={bidModal.open}
        talent={bidModal.talent}
        talentType={bidModal.talentType}
        skillListingId={bidModal.skillListingId}
        skillListingIds={bidModal.skillListingIds}
        onClose={() => {
          if (bidModal.skillListingIds?.length) setSelectedTalentIds([])
          setBidModal({ open: false, talent: null, talentType: null, skillListingId: null, skillListingIds: null })
        }}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => {
            setSelectedTalentIds([]);
            const nextState = updateQueryState({ currentPage: page });
            fetchMarketplaceTalents(
              nextState.currentPage,
              nextState.filters,
              nextState.sortOrder
            );
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}
    </div>
  );
};

export default MarketplaceTalentResultsPage;
